/**
 * @file schedule.page.tsx
 * @description Компонент страницы диаграммы расписания бэклога для визуализации диаграммы Ганта для 
 * планирования проектов и управления временными рамками. Включает комплексную визуализацию временных рамок задач с 
 * иерархической структурой дерева, индикаторами статуса, отслеживанием трудозатрат и назначением исполнителей. 
 * Включает продвинутую фильтрацию по статусу задач, проекту и ответственному лицу, плюс настраиваемые 
 * параметры видимости столбцов для персонализированных представлений диаграммы. Предназначено для детального 
 * планирования проектов и анализа расписания.
 * @author AI Assistant
 * @created 2025-09-04
 * @updated 2025-09-04
 * @dependencies ['@features/filter/types', '@src/utils/table-view', 'react-dom/server', '@components/icons/info-circle']
 * @related-files ['@src/utils/table-view/getGanttStateIcon.tsx']
 */

import { filterComponent } from '@features/filter/types';
import { getDateTimeCell, getDefaultCell, getGanttStateIcon } from '@src/utils/table-view';
import { renderToStaticMarkup } from 'react-dom/server';
import { InfoCircleIcon } from '@components/icons/info-circle';

export const title = 'Бэклог';
export const type = 'gantt';
export const name = 'Schedule';
export const path = 'chart-schedule';

export const columns = [
    {
        name: 'code',
        label: 'Код',
        tree: true,
        resize: true,
        width: 140,
        template: ({ $item }: any) => renderToStaticMarkup(getDefaultCell($item))
    },
    {
        name: 'comment',
        label: 'Описание',
        resize: true,
        width: 220,
        onrender: 'link'
    },
    {
        name: 'state',
        label: renderToStaticMarkup(
            <div className="h-full min-w-[2.1164vmin] flex items-center justify-center">
                <InfoCircleIcon />
            </div>
        ),
        resize: true,
        width: 30,
        min_width: 30,
        template: ({ state }: any) =>
            renderToStaticMarkup(
                <div className="flex h-full items-center justify-center">{getGanttStateIcon(state)}</div>
            )
    },
    {
        name: 'laboriousness',
        label: 'ТР',
        resize: true,
        width: 40,
        min_width: 30,
        align: 'left',
        template: ({ laboriousness }: any) => laboriousness && renderToStaticMarkup(getDefaultCell(`${laboriousness}д`))
    },
    {
        name: 'start_date',
        label: 'Начало',
        resize: true,
        width: 90,
        template: ({ start_date }: any) => renderToStaticMarkup(getDateTimeCell(start_date, 'dd DD.MM.YY'))
    },
    {
        name: 'end_date',
        label: 'Окончание',
        resize: true,
        width: 90,
        template: ({ end_date }: any) => renderToStaticMarkup(getDateTimeCell(end_date, 'dd DD.MM.YY'))
    },
    {
        name: 'executor',
        label: 'Исполнитель',
        width: 150,
        resize: true,
        template: ({ executor }: any) => renderToStaticMarkup(getDefaultCell(executor))
    }
    // { name: 'add', label: '', width: 44 }
];

export const getFilterControls = (data: any = {}): filterComponent[] => [
    {
        filterType: 'select',
        filterProps: {
            label: 'Статус задачи',
            emptyItem: 'Все'
        },
        value: data.state,
        type: 'ETaskState',
        name: 'state',
        tab: 'filters'
    },
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Проект',
            type: 'Project'
        },
        value: data.project,
        name: 'project',
        tab: 'filters'
    },
    {
        filterType: 'dictionary',
        filterProps: {
            label: 'Ответственный',
            type: 'User'
        },
        value: data.executor,
        name: 'executor',
        tab: 'filters'
    },
    {
        filterType: 'columnVisibility',
        filterProps: {
            label: 'Отображать статус'
        },
        targetColumn: 'state',
        value: data.showState,
        name: 'showState',
        tab: 'settings'
    },
    {
        filterType: 'columnVisibility',
        filterProps: {
            label: 'Отображать исполнителя'
        },
        targetColumn: 'executor',
        value: data.showExecutor,
        name: 'showExecutor',
        tab: 'settings'
    },
    {
        filterType: 'columnsSettings',
        value: data.columnsSettings,
        name: 'columnsSettings',
        tab: 'settings'
    }
];
